import Link from 'next/link';
import Image from 'next/image';

import { Button } from '@paction-2.0/ui';
import { ChipField } from '@paction-2.0/ui/components/ChipField';
import { CollapsibleContainer } from '@paction-2.0/ui/components/CollapsibleContainer';

import type { CalculatorDto } from '@/lib/api/tools';
import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';
import type {
  SelectionMap,
  SelectionValue
} from '../hooks/useCostCalculatorState';
import { useCalculatorSections } from './useCalculatorSections';
import styles from './calculatorSections.module.scss';
import InsuranceWarning from './InsuranceWarning';

export type CalculatorSectionsProps = {
  sections: CalculatorDto['sections'];
  outputs: CalculatorDto['outputs'];
  selections: SelectionMap;
  expandedSections: Record<string, boolean>;
  sectionTotals: Record<string, Record<string, number>>;
  breed?: CostCalculatorBreed | null;
  defaultOutput?: { legacyId: string } | null;
  onToggleSection: (legacyId: string) => void;
  onSelectOption: (questionId: string, value: SelectionValue) => void;
  onExpandNext: (currentIndex: number) => void;
  onRecalculate?: () => void;
  stepLinks: Array<{ href: string; label: string; icon: string; alt: string }>;
};

const CalculatorSections = ({
  sections,
  outputs,
  selections,
  expandedSections,
  sectionTotals,
  breed,
  defaultOutput,
  onToggleSection,
  onSelectOption,
  onExpandNext,
  onRecalculate,
  stepLinks
}: CalculatorSectionsProps) => {
  const { sectionViews } = useCalculatorSections({
    sections,
    outputs,
    selections,
    expandedSections,
    sectionTotals,
    breed,
    defaultOutput
  });

  return (
    <>
      <section className={styles.stepsSection} aria-label="Calculator steps">
        <h2>4-Step Calculator</h2>
        <div className={styles.stepsGrid}>
          {stepLinks.map((step) => (
            <Link key={step.href} href={step.href} className={styles.stepCard}>
              <span className={styles.stepIcon}>
                <Image src={step.icon} alt={step.alt} fill sizes="160px" />
              </span>
              <span className={styles.stepLabel}>{step.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {sectionViews.map((section, index) => {
        const expandNext = () => onExpandNext(index);

        return (
          <CollapsibleContainer
            key={section.legacyId}
            id={section.anchor ?? undefined}
            className={styles.sectionGroup}
            titleClassName={styles.sectionTitle}
            title={section.title}
            headingLevel={3}
            collapsed={!section.isExpanded}
            labelWhenCollapsed={section.collapsedLabel}
            onToggle={(nextCollapsed) => {
              onToggleSection(section.legacyId);
              if (!nextCollapsed) {
                const el = section.anchor
                  ? document.getElementById(section.anchor)
                  : null;
                if (el)
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            <div className={styles.sectionBody}>
              <div className={styles.sectionContent}>
                {section.questions.map((question) => (
                  <div key={question.legacyId} className={styles.question}>
                    <ChipField
                      label={question.label}
                      options={question.options}
                      selectedOptions={question.selectedOptions}
                      onChange={(next) =>
                        onSelectOption(
                          question.legacyId,
                          next as SelectionValue
                        )
                      }
                      multiSelect={question.isMulti}
                      headingLevel={4}
                    />

                    {question.showInsuranceWarning ? (
                      <div className={styles.insuranceWarningInline}>
                        <InsuranceWarning breed={breed} />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              {section.showInsuranceAside ? (
                <div className={styles.insuranceWarningAside}>
                  <InsuranceWarning breed={breed} />
                </div>
              ) : null}

              {section.ad ? (
                <Link
                  href="/dog-cost-calculator/paction-picks"
                  className={styles.sectionAd}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className={styles.sectionAdTitle}>
                    {section.ad.title}
                  </div>
                  {section.ad.imageUrl ? (
                    <div className={styles.sectionAdImage}>
                      <Image
                        className={styles.sectionAdImageImg}
                        src={section.ad.imageUrl}
                        alt={section.ad.imageAlt ?? ''}
                        fill
                        sizes="144px"
                      />
                    </div>
                  ) : null}
                  <div className={styles.sectionAdText}>{section.ad.text}</div>
                </Link>
              ) : null}
            </div>

            <div className={styles.sectionFooter}>
              {section.defaultLabel ? (
                <div className={styles.sectionAmount}>
                  <span className={styles.sectionAmountLabel}>Estimate:</span>
                  <span className={styles.sectionAmountValue}>
                    {section.defaultLabel}
                  </span>
                  {section.otherTotals.map((total) => (
                    <span
                      key={total.legacyId}
                      className={styles.sectionAmountSubLabel}
                    >
                      ({total.label}: ${total.amount.toFixed(2)})
                    </span>
                  ))}
                </div>
              ) : (
                <div />
              )}
              {onRecalculate ? (
                <Button
                  type="button"
                  onClick={onRecalculate}
                  className={styles.nextButton}
                >
                  Recalculate
                </Button>
              ) : index < sectionViews.length - 1 ? (
                <Button
                  type="button"
                  onClick={expandNext}
                  className={styles.nextButton}
                >
                  Next costs
                </Button>
              ) : null}
            </div>
          </CollapsibleContainer>
        );
      })}
    </>
  );
};

export default CalculatorSections;
